import type { SnapCategory } from '@/types/database.types';
import { getCategoryColor } from '@/utils/snap';

interface CategorySelectProps {
  selectedCategories: SnapCategory[];
  onChange: (categories: SnapCategory[]) => void;
  maxCategories?: number;
}

const categories: SnapCategory[] = [
  'politics',
  'economy',
  'finance',
  'relationships',
  'lifestyle',
  'technology',
  'health',
  'society_ethics',
  'entertainment',
  'personal_growth'
];

const categoryDescriptions = {
  politics: 'Society, elections, government, laws',
  economy: 'Economy, labor market, companies',
  finance: 'Money, cryptocurrencies, stocks, expenses',
  relationships: 'Love, friendship, dating, family',
  lifestyle: 'Nutrition, fashion, travel, hobbies',
  technology: 'Apps, AI, social media, new devices',
  health: 'Body, mental health, medicine, fitness',
  society_ethics: 'Morals, values, equality, culture',
  entertainment: 'Movies, series, music, games, celebrities',
  personal_growth: 'Decisions, motivation, career, education'
};

export default function CategorySelect({ selectedCategories, onChange, maxCategories = 3 }: CategorySelectProps) {
  const handleToggleCategory = (category: SnapCategory) => {
    if (selectedCategories.includes(category)) {
      onChange(selectedCategories.filter(c => c !== category));
    } else if (selectedCategories.length < maxCategories) {
      onChange([...selectedCategories, category]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">
        Select up to {maxCategories} categories that best describe your SNAP
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {categories.map((cat) => {
          const isSelected = selectedCategories.includes(cat);
          const colors = getCategoryColor(cat);
          return (
            <label
              key={cat}
              className={`relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none transition-colors ${
                isSelected
                  ? 'border-primary-500 ring-2 ring-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-primary-400 bg-white'
              } ${selectedCategories.length >= maxCategories && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => handleToggleCategory(cat)}
            >
              <span className="flex flex-1">
                <span className="flex flex-col">
                  <span className="flex items-center text-sm font-medium text-gray-900">
                    <span className="mr-2">{colors.icon}</span>
                    <span className="capitalize">{cat.replace('_', ' ')}</span>
                  </span>
                  <span className="mt-1.5 text-sm text-gray-500">
                    {categoryDescriptions[cat]}
                  </span>
                </span>
              </span>
              {isSelected && (
                <span className="absolute -inset-px rounded-lg border-2 border-primary-500 pointer-events-none" aria-hidden="true" />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}